import { useState, useEffect } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { useAuth } from '../../common/context/AuthContext'
import { Printer, ArrowLeft, Clock, Calendar, AlertTriangle } from "lucide-react"
import { API_URL } from '../../api/config'

const PrintKOT = () => {
  const [kotData, setKotData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [printing, setPrinting] = useState(false)
  const [error, setError] = useState(null)
  const { userToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { tableId } = useParams()

  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  const formattedTime = currentDate.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    console.log('Location state:', location.state)
    console.log('Table ID from params:', tableId)
    
    if (location.state) {
      const { currentBill, tableNumber } = location.state
      console.log('Current bill:', currentBill)
      setKotData({
        ...currentBill,
        tableNumber: tableNumber
      })
      setLoading(false)
    } else {
      setError('No bill data found')
      setLoading(false)
    }
  }, [location.state, navigate, tableId])

  const handlePrint = async () => {
    setPrinting(true)
    setError(null)
    
    try {
      // Comprehensive validation
      const validationErrors = []
      
      if (!tableId) {
        validationErrors.push('Table ID is missing')
      }
      
      if (!userToken) {
        validationErrors.push('Authentication token is missing')
      }
      
      if (!kotData?.items || kotData.items.length === 0) {
        validationErrors.push('No items found in the bill')
      }

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(', '))
      }

      console.log('=== KOT Creation Debug Info ===')
      console.log('API_URL:', API_URL)
      console.log('Request URL:', `${API_URL}/kot`)
      console.log('Table ID:', tableId)
      console.log('Auth token (first 20 chars):', userToken?.substring(0, 20) + '...')
      console.log('Items count:', kotData.items?.length)
      console.log('Full kotData:', kotData)

      // Prepare request body - only tableId and holdBillId needed
      const requestBody = {
        tableId: tableId,
        holdBillId: kotData._id || kotData.id
      }

      console.log('Request body:', requestBody)

      // Make the API request
      const response = await fetch(`${API_URL}/kot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(requestBody),
      })

      console.log('Response status:', response.status)
      console.log('Response status text:', response.statusText)
      
      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response.text()
      console.log('Raw response text:', responseText)
      
      let result
      try {
        result = JSON.parse(responseText)
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError)
        throw new Error(`Server returned invalid response. Status: ${response.status}, Response: ${responseText}`)
      }
      
      console.log('Parsed KOT API Response:', result)
      
      if (!response.ok) {
        const errorMsg = result.message || result.error || `HTTP Error: ${response.status} - ${response.statusText}`
        throw new Error(errorMsg)
      }

      console.log('KOT creation successful, proceeding with print...')

      // Use browser's native print function
      window.print()
      
    } catch (error) {
      console.error('Error in handlePrint:', error)
      setError(error.message || 'Unknown error occurred')
    } finally {
      setPrinting(false)
    }
  }

  const getTotalQuantity = () => {
    return kotData?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0
  }

  // Helper function to get item name from various possible field names
  const getItemName = (item) => {
    return item.itemName || item.item_name || item.name || 'Unknown Item'
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-base text-black">Loading KOT data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 p-4">
        <AlertTriangle size={48} className="text-red-500" />
        <p className="mt-4 text-lg text-black mb-6">{error}</p>
        <button
          className="py-2 px-4 bg-gray-600 text-white rounded-lg"
          onClick={handleBack}
        >
          <div className="flex items-center">
            <ArrowLeft size={16} className="mr-2" />
            <span>Go Back</span>
          </div>
        </button>
      </div>
    )
  }

  if (!kotData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 p-4">
        <AlertTriangle size={48} className="text-red-500" />
        <p className="mt-4 text-lg text-black mb-6">No KOT Data Found</p>
        <button
          className="py-2 px-4 bg-gray-600 text-white rounded-lg"
          onClick={handleBack}
        >
          <div className="flex items-center">
            <ArrowLeft size={16} className="mr-2" />
            <span>Go Back</span>
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Print-only styles */}
      <style type="text/css" media="print">
        {`
          @page { size: 80mm 297mm; margin: 0; }
          .no-print { display: none !important; }
          .print-kot { width: 100%; max-width: 100%; }
          body { width: 80mm; margin: 0; padding: 0; }
          /* Force dark text when printing */
          p, span, div, td, th, h1, h2, h3 { color: black !important; font-weight: 600 !important; }
          .print-bold { font-weight: 800 !important; }
        `}
      </style>

      <div className="container mx-auto max-w-md px-4 py-6 mb-20 print-kot">
        {/* Error Display - No Print */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg no-print">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700 font-medium">Error</p>
            </div>
            <p className="text-red-600 mt-2 text-sm">{error}</p>
          </div>
        )}

        {/* KOT Header */}
        <div className="text-center border-b-2 border-black py-6">
          <p className="text-6xl text-black">KOT</p>
        </div>

        <div className="p-4">
          {/* Date and Time */}
          <div className="flex justify-between items-center text-lg mb-6 border-b-2 border-black pb-4">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-black" />
              <span className="text-black font-semibold">Date: {formattedDate}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2 text-black" />
              <span className="text-black font-semibold">Time: {formattedTime}</span>
            </div>
          </div>

          {/* Table Information */}
          <div className="text-center mb-6 border-b-2 border-black pb-4">
            <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4">
              <h3 className="text-3xl font-bold text-black print-bold">
                TABLE {kotData.tableNumber}
              </h3>
            </div>
          </div>

          {/* Items List */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4 pb-2 border-b-2 border-black">
              <span className="font-bold text-xl text-black print-bold">ITEM</span>
              <span className="font-bold text-xl text-black print-bold">QTY</span>
            </div>

            <div className="space-y-4">
              {kotData.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-start border-b border-gray-400 pb-2">
                  <div className="flex-1 pr-4">
                    <p className="font-semibold text-lg text-black">{getItemName(item)}</p>
                    {item.variant && (
                      <p className="text-base text-black">({item.variant.name})</p>
                    )}
                    {item.note && (
                      <p className="text-sm text-black italic">Note: {item.note}</p>
                    )}
                  </div>
                  <div className="font-bold text-xl text-black min-w-[40px] text-right print-bold">
                    {item.quantity}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total Items */}
          <div className="mb-6 border-t-2 border-black pt-4">
            <div className="bg-yellow-100 border-2 border-yellow-300 rounded-lg p-4 text-center">
              <span className="font-bold text-xl text-black print-bold">
                Total Items: {getTotalQuantity()}
              </span>
            </div>
          </div>

          {/* Additional Information */}
          {kotData.names && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-lg text-black">
                <span className="font-bold text-black print-bold">Bill Name:</span>
                <span className="ml-2">{kotData.names}</span>
              </p>
            </div>
          )}

          {kotData.mobileNumber && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-lg text-black text-center">
                <span className="font-bold text-black print-bold">Mobile:</span>
                <span className="ml-2">{kotData.mobileNumber}</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Button Container at bottom of screen - No Print */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-black p-4 z-10 no-print">
        <div className="container mx-auto max-w-md flex justify-between">
          <button
            className="w-1/3 bg-gray-600 hover:bg-gray-700 text-white py-3 px-6 rounded-lg mr-4 flex items-center justify-center transition-colors"
            onClick={handleBack}
          >
            <ArrowLeft size={18} className="mr-2" />
            <span>Back</span>
          </button>
            
          <button
            className="w-2/3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white py-3 px-6 rounded-lg flex items-center justify-center transition-colors"
            onClick={handlePrint}
            disabled={printing || !tableId || !kotData?._id || !kotData?.items?.length}
          >
            {printing ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            ) : (
              <>
                <Printer size={20} className="mr-2" />
                <span>Print KOT</span>
              </>
            )}
          </button>
        </div>
        
        {/* Validation Messages */}
        {(!tableId || !kotData?._id || !kotData?.items?.length) && (
          <div className="container mx-auto max-w-md mt-2">
            <p className="text-sm text-red-600 text-center">
              {!tableId && 'Missing Table ID '}
              {!kotData?._id && 'Missing Hold Bill ID '}
              {!kotData?.items?.length && 'No items in bill'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PrintKOT